import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import type { Task } from "@/types/task";
import { TaskList } from "@/components/tasks/TaskList";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { Button } from "@/components/ui/button";
import { CalendarCheck2, Plus, RefreshCw } from "lucide-react";

interface LeadTasksSectionProps {
  leadId: string;
  leadName: string;
  onFollowUpChanged?: () => void;
}

export function LeadTasksSection({
  leadId,
  leadName,
  onFollowUpChanged,
}: LeadTasksSectionProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/api/Task/List?related_type=LEAD&related_id=${leadId}&scope=all&page_size=100`
      );
      const data = unwrapPage<Task>(res).rows;
      setTasks(data);
    } catch (err) {
      console.warn("Could not load tasks for lead:", err);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleRefresh = () => {
    fetchTasks();
    if (onFollowUpChanged) onFollowUpChanged();
  };

  const openCount = tasks.filter((t) => t.task_status === "OPEN").length;

  return (
    <div className="space-y-3 pt-3 border-t border-border">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarCheck2 className="size-4 text-primary" />
          <span className="font-semibold text-sm text-foreground">
            Follow-Up Tasks & Reminders
          </span>
          {openCount > 0 && (
            <span className="text-[11px] font-bold px-1.5 py-0.2 rounded-full bg-primary/10 text-primary">
              {openCount} active
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={handleRefresh}
            title="Refresh tasks"
            className="size-7"
          >
            <RefreshCw className="size-3.5 text-muted-foreground" />
          </Button>

          <Button
            size="sm"
            onClick={() => setCreateDialogOpen(true)}
            className="h-7 text-xs flex items-center gap-1"
          >
            <Plus className="size-3.5" />
            <span>Schedule Follow-Up</span>
          </Button>
        </div>
      </div>

      <TaskList
        tasks={tasks}
        loading={loading}
        onRefresh={handleRefresh}
        showAssignee={true}
        emptyMessage="No tasks scheduled for this lead. Click 'Schedule Follow-Up' above."
      />

      {/* Add Task Dialog */}
      <TaskDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        defaultRelated={{
          type: "LEAD",
          id: leadId,
          name: leadName,
        }}
        onSaved={handleRefresh}
      />
    </div>
  );
}
